import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';

import { useSession } from '@/hooks/useSession';
import { getProfile, type UserProfile } from '@/lib/db/profile';

const isDev = import.meta.env.DEV;

export type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | {
      status: 'authenticated';
      user: { id: string; email: string };
      profile: UserProfile;
    };

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const { loading: sessionLoading, session } = useSession();
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const userId = session?.user.id;
  const userEmail = session?.user.email;

  useEffect(() => {
    if (sessionLoading) {
      setState({ status: 'loading' });
      return;
    }
    if (!userId) {
      setState({ status: 'anonymous' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const profile = await getProfile(userId);
        if (cancelled) return;
        if (!profile) {
          // Session exists but no public.users row — RLS rejection or trigger
          // failure during signup. Treat as anonymous so RequireRole 403s
          // rather than crashing on a partial state.
          if (isDev) console.warn('AuthContext: profile not found for session user');
          setState({ status: 'anonymous' });
          return;
        }
        // Patch 5 — refuse non-active or soft-deleted accounts at the auth
        // root so that a disabled admin (status='disabled') or a tombstoned
        // user (deleted_at IS NOT NULL) cannot reach /admin or /dashboard
        // even with a valid Supabase session. RequireRole sees 'anonymous'.
        if (profile.status !== 'active' || profile.deleted_at !== null) {
          if (isDev)
            console.warn('AuthContext: profile not active', {
              status: profile.status,
              deleted: profile.deleted_at !== null,
            });
          setState({ status: 'anonymous' });
          return;
        }
        setState({
          status: 'authenticated',
          user: { id: userId, email: userEmail ?? '' },
          profile,
        });
      } catch (err) {
        if (cancelled) return;
        if (isDev) console.warn('AuthContext: profile fetch failed', err);
        setState({ status: 'anonymous' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionLoading, userId, userEmail]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with AuthProvider for cohesion; HMR cost is acceptable for an auth root provider that rarely changes shape.
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
